import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { isAfter } from 'date-fns';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { SuccessResp } from '@paradigm/api/src';
import { listPositions } from '#/positions/repositories/positions';
import {
  PositionsNotification,
  PositionsWebSocket,
} from '#/positions/entities/types';
import { usePositionsSubscriberStatus } from '#/positions/contexts/subscriber';
import { usePositionsWebsocket } from '#/positions/contexts/websocket';
import {
  usePositionsActions,
  usePositionsState,
} from '#/positions/contexts/store';
import {
  ListPositionsResult,
  Position,
  filterPositions,
  parseFiltersToProductCodes,
} from '#/positions/entities/positions';
import {
  FetcherState,
  useFetcher,
} from '#/positions/contexts/abstractions/useFetcher';
import { usePositionFiltersState } from '#/positions/contexts/filters/store';

export const [usePositionsFetcherState, PositionsFetcherContext] =
  createSafeContext<FetcherState>('PositionsFetcherState');

const channel = 'position_updates';

interface Props {
  children: ReactNode;
}

export default function PositionsFetcherProvider({ children }: Props) {
  const ws = usePositionsWebsocket();
  const subStatus = usePositionsSubscriberStatus();
  const { setAll } = usePositionsActions();
  const filters = usePositionFiltersState();
  const { lastUpdatedAt } = usePositionsState();
  const lastUpdatedAtRef = useRef<Date | null>(null);

  lastUpdatedAtRef.current = lastUpdatedAt;

  const applySnapshot = useCallback(
    (result: SuccessResp<ListPositionsResult<Position>>) =>
      setAll(result.data.results, result.data.last_updated_at),
    [setAll],
  );

  const applyWSNotification = useCallback(
    (notif: PositionsNotification) => {
      if (
        // prevent race condition between ws messages and snapshot request
        lastUpdatedAtRef.current == null ||
        isAfter(notif.params.data.last_updated_at, lastUpdatedAtRef.current)
      ) {
        setAll(
          filterPositions(notif.params.data.positions, filters),
          notif.params.data.last_updated_at,
        );
      }
    },
    [filters, setAll],
  );

  const req = useMemo(
    () => ({
      accounts: filters.accounts,
      productCodes: parseFiltersToProductCodes(filters),
    }),
    [filters],
  );

  const fetchSnapshot = useCallback(async () => listPositions(req), [req]);

  const state = useFetcher<
    PositionsWebSocket,
    PositionsNotification,
    ListPositionsResult<Position>
  >(
    ws,
    channel,
    subStatus.status,
    applySnapshot,
    applyWSNotification,
    fetchSnapshot,
  );

  return (
    <PositionsFetcherContext value={state}>{children}</PositionsFetcherContext>
  );
}
